import { useCallback, useMemo } from "react";
import { useAppDispatch } from "hooks/redux";
import {
  websiteBuilderDeleteNode,
  websiteBuilderBringToFrontElement,
  websiteBuilderBringToBackElement,
  websiteBuilderSwapFlexChild,
  websiteBuilderSwapGridCellContent,
} from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { isWebsiteBuilderStretchElementData, TWebsiteBuilderElement } from "../../../nodes/element/websiteBuilderElement";
import { clamp, cloneDeep } from "lodash";
import { isWebsiteBuilderElementFreeformParentNode, TWebsiteBuilderElementParentNode } from "../../../nodes/websiteBuilderNode";
import {
  TWebsiteBuilderSection,
  websiteBuilderGenerateSectionBounds,
} from "../../../nodes/section/websiteBuilderSection";
import { IWebsiteBuilderClipboard } from "../../clipboard/useWebsiteBuilderClipboard";
import { isWebsiteBuilderSectionEntity } from "../../../nodes/websiteBuilderSectionEntity";
import { TWebsiteBuilderDevice } from "../../../website/shared/device";
import { websiteBuilderSanitize } from "../../../sanitization/websiteBuilderSanitize";
import { isWebsiteBuilderFlex } from "../../../nodes/container/websiteBuilderFlex";
import { isWebsiteBuilderGridCell, websiteBuilderGridCellFilled } from "../../../nodes/gridCell/websiteBuilderGridCell";
import { isWebsiteBuilderBox } from "../../../nodes/container/websiteBuilderBox";
import { TWebsiteBuilderNudgeSectionEntityInFreeformDirection, websiteBuilderCanBringToBackInFreeformParent, websiteBuilderCanBringToFrontInFreeformParent, websiteBuilderDuplicateSectionEntityInFreeform, websiteBuilderInsertSectionEntityInFreeform, websiteBuilderNudgeSectionEntityInFreeform, websiteBuilderGetFreeformParentNextZIndex, IWebsiteBuilderFreeformParentContext, TWebsiteBuilderFreeformParentBounds } from "../../../nodes/websiteBuilderFreeformParentNode";
import { websiteBuilderInsertElementIntoFlexContainer, websiteBuilderInsertElementIntoNextGridCell, websiteBuilderGetGridCellAncestry, websiteBuilderGetTabAncestry } from "../helpers/sectionEntityOperations";
import { isWebsiteBuilderTab, websiteBuilderGenerateTabDimensions } from "../../../nodes/tab/websiteBuilderTab";

type TWebsiteBuilderCopyElement = (
  node: TWebsiteBuilderElement,
) => void;

type TWebsiteBuilderPasteAtElement = (
  targetNode: TWebsiteBuilderElement,
  targetParentNode: TWebsiteBuilderElementParentNode,
  targetSectionNode: TWebsiteBuilderSection,
  pageId: number,
  device: TWebsiteBuilderDevice,
) => void;

type TWebsiteBuilderDuplicateElement = (
  node: TWebsiteBuilderElement,
  parentNode: TWebsiteBuilderElementParentNode,
  sectionNode: TWebsiteBuilderSection,
  pageId: number,
  device: TWebsiteBuilderDevice,
) => void;

type TWebsiteBuilderBringToFrontElement = (
  node: TWebsiteBuilderElement,
  parentNode: TWebsiteBuilderElementParentNode,
  pageId: number,
  device: TWebsiteBuilderDevice,
) => void;

type TWebsiteBuilderBringToBackElement = (
  node: TWebsiteBuilderElement,
  parentNode: TWebsiteBuilderElementParentNode,
  pageId: number,
  device: TWebsiteBuilderDevice,
) => void;

type TWebsiteBuilderDeleteElement = (
  node: TWebsiteBuilderElement,
  parentNode: TWebsiteBuilderElementParentNode,
  pageId: number,
  device: TWebsiteBuilderDevice,
) => void;

type TWebsiteBuilderCutElement = (
  node: TWebsiteBuilderElement,
  parentNode: TWebsiteBuilderElementParentNode,
  pageId: number,
  device: TWebsiteBuilderDevice,
) => void;

type TWebsiteBuilderNudgeElement = (
  direction: TWebsiteBuilderNudgeSectionEntityInFreeformDirection,
  shift: boolean,
  node: TWebsiteBuilderElement,
  parentNode: TWebsiteBuilderElementParentNode,
  sectionNode: TWebsiteBuilderSection,
  pageId: number,
  device: TWebsiteBuilderDevice,
) => void;

export interface IWebsiteBuilderElementOperations {
  copyElement: TWebsiteBuilderCopyElement;
  pasteAtElement: TWebsiteBuilderPasteAtElement;
  duplicateElement: TWebsiteBuilderDuplicateElement;
  bringToFrontElement: TWebsiteBuilderBringToFrontElement;
  bringToBackElement: TWebsiteBuilderBringToBackElement;
  deleteElement: TWebsiteBuilderDeleteElement;
  cutElement: TWebsiteBuilderCutElement;
  nudgeElement: TWebsiteBuilderNudgeElement;
}

export function useWebsiteBuilderElementOperations(
  clipboard: IWebsiteBuilderClipboard,
): IWebsiteBuilderElementOperations {
  const dispatch = useAppDispatch();

  const copyElement: TWebsiteBuilderCopyElement = useCallback(
    node => {
      clipboard.copy(node);
    },
    [clipboard],
  );

  const pasteAtElement: TWebsiteBuilderPasteAtElement = useCallback(
    (targetNode, targetParentNode, targetSectionNode, pageId, device) => {
      const copiedNode = clipboard.peek();

      if (isWebsiteBuilderSectionEntity(copiedNode) && targetParentNode.type === "section") {
        const parentContext: IWebsiteBuilderFreeformParentContext = {
          node: targetParentNode,
          bounds: {
            desktop: websiteBuilderGenerateSectionBounds(targetParentNode.section.layout, "desktop"),
            tablet: websiteBuilderGenerateSectionBounds(targetParentNode.section.layout, "tablet"),
            mobile: websiteBuilderGenerateSectionBounds(targetParentNode.section.layout, "mobile"),
          },
          ancestry: {
            sectionId: targetSectionNode.id,
            containerId: null,
            gridCellId: null,
            tabId: null,
          },
        };
        
        websiteBuilderInsertSectionEntityInFreeform(
          copiedNode,
          targetNode,
          parentContext,
          pageId,
          device,
          dispatch,
        );
      } else if (
        copiedNode?.type === "element" && (
          !isWebsiteBuilderStretchElementData(copiedNode.element) ||
          !copiedNode.element.stretch.enabled
        )
      ) {
        if (isWebsiteBuilderBox(targetParentNode)) {
          const parentContext: IWebsiteBuilderFreeformParentContext = {
            node: targetParentNode,
            bounds: {
              desktop: targetParentNode.container.layout.desktop.dimensions,
              tablet: targetParentNode.container.layout.tablet.dimensions,
              mobile: targetParentNode.container.layout.mobile.dimensions,
            },
            ancestry: {
              sectionId: targetSectionNode.id,
              containerId: targetParentNode.id,
              gridCellId: null,
              tabId: null,
            },
          };

          websiteBuilderInsertSectionEntityInFreeform(
            copiedNode,
            targetNode,
            parentContext,
            pageId,
            device,
            dispatch,
          );
        } else if (isWebsiteBuilderFlex(targetParentNode)) {
          websiteBuilderInsertElementIntoFlexContainer(copiedNode, targetParentNode, targetSectionNode.id, pageId, device, dispatch);
        } else if (isWebsiteBuilderGridCell(targetParentNode)) {
          websiteBuilderInsertElementIntoNextGridCell(copiedNode, targetParentNode, targetSectionNode, pageId, device, dispatch);
        } else if (isWebsiteBuilderTab(targetParentNode)) {
          const tabAncestry = websiteBuilderGetTabAncestry(targetSectionNode, targetParentNode);

          if (tabAncestry == null) {
            return;
          }

          const { tabsetNode } = tabAncestry;

          const parentContext: IWebsiteBuilderFreeformParentContext = {
            node: targetParentNode,
            bounds: {
              desktop: websiteBuilderGenerateTabDimensions(tabsetNode, "desktop"),
              tablet: websiteBuilderGenerateTabDimensions(tabsetNode, "tablet"),
              mobile: websiteBuilderGenerateTabDimensions(tabsetNode, "mobile"),
            },
            ancestry: {
              sectionId: targetSectionNode.id,
              containerId: tabsetNode.id,
              gridCellId: null,
              tabId: targetParentNode.id,
            },
          };

          websiteBuilderInsertSectionEntityInFreeform(
            copiedNode,
            targetNode,
            parentContext,
            pageId,
            device,
            dispatch,
          );
        }
      }
    },
    [clipboard],
  );

  const duplicateElement: TWebsiteBuilderDuplicateElement = useCallback((node, parentNode, sectionNode, pageId, device) => {
    if (parentNode.type === "section") {
      const nodeToDuplicate: TWebsiteBuilderElement = websiteBuilderSanitize(node);

      const parentContext: IWebsiteBuilderFreeformParentContext = {
        node: parentNode,
        bounds: {
          desktop: websiteBuilderGenerateSectionBounds(parentNode.section.layout, "desktop"),
          tablet: websiteBuilderGenerateSectionBounds(parentNode.section.layout, "tablet"),
          mobile: websiteBuilderGenerateSectionBounds(parentNode.section.layout, "mobile"),
        },
        ancestry: {
          sectionId: sectionNode.id,
          containerId: null,
          gridCellId: null,
          tabId: null,
        },
      };

      websiteBuilderDuplicateSectionEntityInFreeform(
        nodeToDuplicate,
        parentContext,
        pageId,
        device,
        dispatch,
      );
    } else if (
      (
        !isWebsiteBuilderStretchElementData(node.element) ||
        !node.element.stretch.enabled
      )
    ) {
      const nodeToDuplicate: TWebsiteBuilderElement = websiteBuilderSanitize(node);

      if (isWebsiteBuilderBox(parentNode)) {
        const parentContext: IWebsiteBuilderFreeformParentContext = {
          node: parentNode,
          bounds: {
            desktop: parentNode.container.layout.desktop.dimensions,
            tablet: parentNode.container.layout.tablet.dimensions,
            mobile: parentNode.container.layout.mobile.dimensions,
          },
          ancestry: {
            sectionId: sectionNode.id,
            containerId: parentNode.id,
            gridCellId: null,
            tabId: null,
          },
        };

        websiteBuilderDuplicateSectionEntityInFreeform(
          nodeToDuplicate,
          parentContext,
          pageId,
          device,
          dispatch,
        );
      } else if (isWebsiteBuilderFlex(parentNode)) {
        websiteBuilderInsertElementIntoFlexContainer(nodeToDuplicate, parentNode, sectionNode.id, pageId, device, dispatch);
      } else if (isWebsiteBuilderGridCell(parentNode)) {
        websiteBuilderInsertElementIntoNextGridCell(nodeToDuplicate, parentNode, sectionNode, pageId, device, dispatch);
      } else if (isWebsiteBuilderTab(parentNode)) {
        const tabAncestry = websiteBuilderGetTabAncestry(sectionNode, parentNode);

        if (tabAncestry == null) {
          return;
        }

        const { tabsetNode } = tabAncestry;

        const parentContext: IWebsiteBuilderFreeformParentContext = {
          node: parentNode,
          bounds: {
            desktop: websiteBuilderGenerateTabDimensions(tabsetNode, "desktop"),
            tablet: websiteBuilderGenerateTabDimensions(tabsetNode, "tablet"),
            mobile: websiteBuilderGenerateTabDimensions(tabsetNode, "mobile"),
          },
          ancestry: {
            sectionId: sectionNode.id,
            containerId: tabsetNode.id,
            gridCellId: null,
            tabId: parentNode.id,
          },
        };

        websiteBuilderDuplicateSectionEntityInFreeform(
          nodeToDuplicate,
          parentContext,
          pageId,
          device,
          dispatch,
        );
      }
    }
  }, []);

  const bringToFrontElement: TWebsiteBuilderBringToFrontElement = useCallback((node, parentNode, pageId, device) => {
    if (
      isWebsiteBuilderElementFreeformParentNode(parentNode) &&
      websiteBuilderCanBringToFrontInFreeformParent(node, parentNode)
    ) {
      dispatch(
        websiteBuilderBringToFrontElement(pageId, device, node.id, {
          id: node.id,
          prevZIndex: node.element.zIndex,
          nextZIndex: websiteBuilderGetFreeformParentNextZIndex(parentNode),
        }),
      );
    }
  }, []);

  const bringToBackElement: TWebsiteBuilderBringToBackElement = useCallback((node, parentNode, pageId, device) => {
    if (
      isWebsiteBuilderElementFreeformParentNode(parentNode) &&
      websiteBuilderCanBringToBackInFreeformParent(node, parentNode)
    ) {
      dispatch(
        websiteBuilderBringToBackElement(pageId, device, node.id, {
          id: node.id,
          parentId: parentNode.id,
          originalZIndex: node.element.zIndex,
          inverse: false,
        }),
      );
    }
  }, []);

  const deleteElement: TWebsiteBuilderDeleteElement = useCallback((node, parentNode, pageId, device) => {
    let index: number = null;

    if (parentNode.type === "grid-cell") {
      index = websiteBuilderGridCellFilled(parentNode) && parentNode.children[0].id === node.id ? 0 : -1;
    } else {
      index = parentNode.children.findIndex(child => child.id === node.id);
    }

    if (index == null || index === -1) {
      return;
    }

    dispatch(
      websiteBuilderDeleteNode(pageId, device, parentNode.id, {
        snapshot: {
          inverseScrollNodeId: node.id,
          parentId: parentNode.id,
          node: cloneDeep(node),
          index,
        },
      }),
    );
  }, []);

  const cutElement: TWebsiteBuilderCutElement = useCallback(
    (node, parentNode, pageId, device) => {
      copyElement(node);
      deleteElement(node, parentNode, pageId, device);
    },
    [copyElement, deleteElement],
  );

  const nudgeElement: TWebsiteBuilderNudgeElement = useCallback(
    (direction, shift, node, parentNode, sectionNode, pageId, device) => {
      if (isWebsiteBuilderElementFreeformParentNode(parentNode)) {
        const tabAncestry = parentNode.type === "tab"
          ? websiteBuilderGetTabAncestry(sectionNode, parentNode)
          : null;

        if (parentNode.type === "tab" && tabAncestry == null) {
          return;
        }

        const tabsetNode = tabAncestry?.tabsetNode;

        const calculateBounds = (): TWebsiteBuilderFreeformParentBounds => {
          if (parentNode.type === "section") {
            return {
              desktop: websiteBuilderGenerateSectionBounds(parentNode.section.layout, "desktop"),
              tablet: websiteBuilderGenerateSectionBounds(parentNode.section.layout, "tablet"),
              mobile: websiteBuilderGenerateSectionBounds(parentNode.section.layout, "mobile"),
            };
          } else if (parentNode.type === "tab") {
            return {
              desktop: websiteBuilderGenerateTabDimensions(tabsetNode, "desktop"),
              tablet: websiteBuilderGenerateTabDimensions(tabsetNode, "tablet"),
              mobile: websiteBuilderGenerateTabDimensions(tabsetNode, "mobile"),
            };
          } else {
            return {
              desktop: parentNode.container.layout.desktop.dimensions,
              tablet: parentNode.container.layout.tablet.dimensions,
              mobile: parentNode.container.layout.mobile.dimensions,
            };
          }
        };

        const bounds: TWebsiteBuilderFreeformParentBounds = calculateBounds();
        
        const parentContext: IWebsiteBuilderFreeformParentContext = {
          node: parentNode,
          bounds,
          ancestry: {
            sectionId: sectionNode.id,
            containerId: parentNode.type === "tab" ? tabsetNode.id : isWebsiteBuilderBox(parentNode) ? parentNode.id : null,
            gridCellId: null,
            tabId: parentNode.type === "tab" ? parentNode.id : null,
          },
        };

        websiteBuilderNudgeSectionEntityInFreeform(
          direction,
          shift,
          node,
          parentContext,
          pageId,
          device,
          dispatch,
        );
      } else if (isWebsiteBuilderFlex(parentNode)) {
        const index = parentNode.children.findIndex(child => child.id === node.id);

        if (index === -1) {
          return;
        }

        if ((direction === "left" || direction === "up") && index > 0) {
          dispatch(
            websiteBuilderSwapFlexChild(pageId, device, node.id, {
              id: parentNode.id,
              index,
              direction: "previous",
            }),
          );
        } else if ((direction === "right" || direction === "down") && index < parentNode.children.length - 1) {
          dispatch(
            websiteBuilderSwapFlexChild(pageId, device, node.id, {
              id: parentNode.id,
              index,
              direction: "next",
            }),
          );
        }
      } else if (isWebsiteBuilderGridCell(parentNode)) {
        const gridCellAncestry = websiteBuilderGetGridCellAncestry(sectionNode, parentNode);

        if (gridCellAncestry == null) {
          return;
        }

        const { gridNode, gridCellIndex } = gridCellAncestry;

        const { rows, columns } = gridNode.container.settings;

        const prevIndex = gridCellIndex;
        let nextIndex = null;

        if (device !== "mobile") {
          if (direction === "left") {
            nextIndex = prevIndex % columns > 0 ? prevIndex - 1 : null;
          } else if (direction === "right") {
            nextIndex = prevIndex % columns < columns - 1 ? prevIndex + 1: null;
          } else if (direction === "up") {
            nextIndex = prevIndex - columns >= 0 ? prevIndex - columns : null;
          } else if (direction === "down") {
            nextIndex = prevIndex + columns <= gridNode.children.length - 1 ? prevIndex + columns : null;
          }
        } else {
          const shift = direction === "left" || direction === "up" ? -1 : 1;

          nextIndex = clamp(prevIndex + shift, 0, rows * columns - 1);
        }

        if (nextIndex == null) {
          return;
        }

        const prevGridCellId = parentNode.id;
        const nextGridCellId = gridNode.children[nextIndex]?.id;

        if (nextGridCellId == null || prevGridCellId === nextGridCellId) {
          return;
        }

        dispatch(
          websiteBuilderSwapGridCellContent(pageId, device, node.id, {
            sectionId: sectionNode.id,
            gridId: gridNode.id,
            elementId: node.id,
            prevGridCellId,
            nextGridCellId,
          }),
        );
      }
    }, []);

  const elementOperations: IWebsiteBuilderElementOperations = useMemo(
    () => ({
      copyElement,
      pasteAtElement,
      duplicateElement,
      bringToFrontElement,
      bringToBackElement,
      deleteElement,
      cutElement,
      nudgeElement,
    }),
    [copyElement, pasteAtElement, duplicateElement, bringToFrontElement, bringToBackElement, deleteElement, cutElement, nudgeElement],
  );

  return elementOperations;
}
